import type {
  Appointment,
  HealthcareService,
  Location,
  Patient,
  Practitioner,
  Reference,
  ServiceRequest,
} from '@medplum/fhirtypes'
import type * as appointments from '@seen/core/models/appointments/index'
import { getReferenceString } from '@seen/core/references'
import type { OmitFunctions } from '@seen/core/types'
import { getType, isOfType, now } from '@seen/core/utils'
import { addMinutes } from 'date-fns'
import { compact } from 'lodash-es'
import { createStore } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { createZustandContext } from '#src/utils/zustand/Context.tsx'
import {
  createStoreQueryParamBuilder,
  createURLPersistentStorage,
} from '#src/utils/zustand/zustand.ts'

export const DEFAULT_APPOINTMENT_REQUEST_DURATION_MINUTES = 30

export const ViewType = {
  // https://fullcalendar.io/docs/timegrid-view
  TimeGrid: 'timeGrid',
  // https://fullcalendar.io/docs/list-view
  List: 'list',
  // https://fullcalendar.io/docs/timeline-view
  Timeline: 'resourceTimeline',
} as const
export type ViewType = (typeof ViewType)[keyof typeof ViewType]

// https://fullcalendar.io/docs/daygrid-view#month-view:~:text=related%20options.-,Week%20%26%20Day%20View,-The%20following%20example
export const ViewHorizon = {
  Day: 'Day',
  Week: 'Week',
} as const
export type ViewHorizon = (typeof ViewHorizon)[keyof typeof ViewHorizon]

export type ViewTypeAndHorizon = `${ViewType}${ViewHorizon}`

export type AppointmentFilterResource = Reference<
  Patient | Practitioner | Location | HealthcareService
>

export interface AppointmentRequestSeed {
  patient?: Reference<Patient>
  practitioner?: Reference<Practitioner>
  healthcareService?: Reference<HealthcareService>
}

export type CalendarState = {
  selectedAppointment?: Reference<Appointment>
  setSelectedAppointment: (appointment?: Reference<Appointment>) => void
  windowStart?: string
  windowEnd?: string
  setWindowPeriod: (start: string, end: string) => void
  viewType: ViewType
  setViewType: (viewType: ViewType) => void
  viewHorizon: ViewHorizon
  setViewHorizon: (viewHorizon: ViewHorizon) => void
  setViewTypeAndHorizon: (viewTypeAndHorizon: ViewTypeAndHorizon) => void
  aggregateCalendarFilters: (
    | 'moe'
    | 'clinic'
    | 'day-center'
    | 'external'
    | 'assessment'
    | 'rehab'
  )[]
  setAggregateCalendarFilters: (
    aggregateCalendarFilters: (
      | 'moe'
      | 'clinic'
      | 'day-center'
      | 'external'
      | 'assessment'
      | 'rehab'
    )[]
  ) => void
  appointmentStatuses: Appointment['status'][]
  clearAppointmentStatuses: () => void
  addAppointmentStatus: (status: Appointment['status']) => void
  removeAppointmentStatus: (status: Appointment['status']) => void
  appointmentFilters: AppointmentFilterResource[]
  setAppointmentFilters: (filters: AppointmentFilterResource[]) => void
  addAppointmentFilter: (filter: AppointmentFilterResource) => void
  removeAppointmentFilter: (filter: AppointmentFilterResource) => void
  // appointment request
  showRequestAppointment: boolean
  setShowRequestAppointment: (showRequestAppointment: boolean) => void
  // Opens the request panel with request fields seeded in the same set() —
  // seeding after setShowRequestAppointment(true) would race the open path's
  // rebuild of appointmentFilters from request state.
  openRequestAppointment: (seed?: AppointmentRequestSeed) => void
  requestedAppointmentStart?: string
  requestedAppointmentEnd?: string
  setRequestedAppointmentPeriod: (
    start?: string,
    end?: string,
    references?: (AppointmentFilterResource | undefined)[]
  ) => void
  // Suggested "next available" slots surfaced by the request form, mirrored here
  // so the calendar grid can draw each as a green dashed block. Transient (not
  // URL-persisted); cleared when the request panel closes.
  availableAppointmentSlots?: { start: string; end: string }[]
  setAvailableAppointmentSlots: (
    slots: { start: string; end: string }[]
  ) => void
  requestPatient?: Reference<Patient>
  setRequestPatient: (patient?: Reference<Patient>) => void
  requestHealthcareService?: Reference<HealthcareService>
  setRequestHealthcareService: (
    healthcareService?: Reference<HealthcareService>,
    durationMinutes?: number
  ) => void
  requestPractitioner?: Reference<Practitioner>
  setRequestPractitioner: (practitioner?: Reference<Practitioner>) => void
  // NUCC specialty value for a clinical-specialist visit. URL-persisted with
  // the rest of the request state so a refresh mid-booking doesn't drop it —
  // it drives the provider recommendations, so losing it silently changes
  // which providers the picker surfaces.
  requestSpecialty?: string
  setRequestSpecialty: (specialty?: string) => void
  requestLocation?: Reference<Location>
  setRequestLocation: (location?: Reference<Location>) => void
  requestBasedOn?: Reference<ServiceRequest>
  setRequestBasedOn: (basedOn?: Reference<ServiceRequest>) => void
  requestRequiresTransportation: boolean
  setRequestRequiresTransportation: (requiresTransportation: boolean) => void
  previousViewTypeAndHorizon?: ViewTypeAndHorizon
  setPreviousViewTypeAndHorizon: (
    viewTypeAndHorizon?: ViewTypeAndHorizon
  ) => void
}

const defaultViewType = ViewType.TimeGrid
const defaultViewHorizon = ViewHorizon.Week

const storageOptions = {
  name: 'calendar',
  storage: createJSONStorage<CalendarState>(() =>
    createURLPersistentStorage<CalendarState>()
  ),
}

// The open-path state shared by setShowRequestAppointment(true) and
// openRequestAppointment — one shape so the two open paths cannot drift.
// Seeded values win over whatever request state is already set.
function openedRequestState(
  state: CalendarState,
  seed?: AppointmentRequestSeed
): Partial<CalendarState> {
  const requestPractitioner = seed?.practitioner ?? state.requestPractitioner
  const requestPatient = seed?.patient ?? state.requestPatient
  const requestHealthcareService =
    seed?.healthcareService ?? state.requestHealthcareService
  return {
    showRequestAppointment: true,
    requestedAppointmentStart: undefined,
    requestedAppointmentEnd: undefined,
    requestBasedOn: undefined,
    availableAppointmentSlots: [],
    selectedAppointment: undefined,
    appointmentStatuses: ['booked'],
    requestPractitioner,
    requestPatient,
    requestHealthcareService,
    // The visit type is deliberately NOT folded into the filters: the
    // calendar query ORs filter types, and the shared clinical-specialist
    // service would pull every specialist appointment onto the grid. Users
    // can still filter by service through the filter UI directly.
    appointmentFilters: compact([
      requestPractitioner,
      requestPatient,
      state.requestLocation,
    ]),
  }
}

const defaultState = {
  viewType: defaultViewType,
  viewHorizon: defaultViewHorizon,
  appointmentStatuses: ['booked'],
  appointmentFilters: [],
  showRequestAppointment: false,
  requestRequiresTransportation: false,
  aggregateCalendarFilters: [],
  availableAppointmentSlots: [],
} satisfies Partial<OmitFunctions<CalendarState>>

export const createCalendarStore = (
  initialState?: Partial<OmitFunctions<CalendarState>>
): ReturnType<ReturnType<typeof createStore<CalendarState>>> =>
  createStore(
    persist<CalendarState>(
      (set) => ({
        ...defaultState,
        ...initialState,
        setSelectedAppointment: (appointment) =>
          set({ selectedAppointment: appointment }),
        setWindowPeriod: (start, end) =>
          set({ windowStart: start, windowEnd: end }),
        setViewType: (viewType) => set({ viewType }),
        setViewHorizon: (viewHorizon) => set({ viewHorizon }),
        setViewTypeAndHorizon: (viewTypeAndHorizon) =>
          set({
            viewType: viewTypeAndHorizon
              .replace(ViewHorizon.Day, '')
              .replace(ViewHorizon.Week, '') as ViewType,
            viewHorizon: viewTypeAndHorizon.includes(ViewHorizon.Day)
              ? ViewHorizon.Day
              : ViewHorizon.Week,
          }),
        setAggregateCalendarFilters: (aggregateCalendarFilters) =>
          set({ aggregateCalendarFilters }),
        clearAppointmentStatuses: () => set({ appointmentStatuses: [] }),
        addAppointmentStatus: (status) =>
          set((state) => ({
            appointmentStatuses: [...state.appointmentStatuses, status],
          })),
        removeAppointmentStatus: (status) =>
          set((state) => ({
            appointmentStatuses: state.appointmentStatuses.filter(
              (s) => s !== status
            ),
          })),
        setAppointmentFilters: (filters) =>
          set({ appointmentFilters: filters }),
        addAppointmentFilter: (filter) =>
          set((state) => ({
            appointmentFilters: [
              ...state.appointmentFilters.filter(
                (f) => getReferenceString(f) !== getReferenceString(filter)
              ),
              filter,
            ],
          })),
        removeAppointmentFilter: (filter) =>
          set((state) => ({
            appointmentFilters: state.appointmentFilters.filter(
              (f) => getReferenceString(f) !== getReferenceString(filter)
            ),
          })),

        setShowRequestAppointment: (showRequestAppointment) =>
          set((state) => {
            if (showRequestAppointment) {
              return openedRequestState(state)
            }
            return {
              showRequestAppointment,
              requestedAppointmentStart: undefined,
              requestedAppointmentEnd: undefined,
              requestBasedOn: undefined,
              availableAppointmentSlots: [],
              requestPractitioner: undefined,
              requestPatient: undefined,
              requestLocation: undefined,
              requestHealthcareService: undefined,
              requestSpecialty: undefined,
            }
          }),
        openRequestAppointment: (seed) =>
          set((state) => openedRequestState(state, seed)),
        setRequestedAppointmentPeriod: (start, end, references) =>
          set((state) => {
            let _end = end
            if (!_end && start) {
              _end = addMinutes(
                start,
                DEFAULT_APPOINTMENT_REQUEST_DURATION_MINUTES
              ).toISOString()
            }
            const newState: Partial<CalendarState> = {
              requestedAppointmentStart: start,
              requestedAppointmentEnd: end,
              appointmentStatuses: ['booked'],
            }
            if (!state.showRequestAppointment) {
              newState.showRequestAppointment = true
              newState.selectedAppointment = undefined
            }

            references?.forEach((reference) => {
              const type = reference?.type ?? getType(reference)
              switch (type) {
                case 'Patient':
                  newState.requestPatient = reference as Reference<Patient>
                  break
                case 'Practitioner':
                  newState.requestPractitioner =
                    reference as Reference<Practitioner>
                  break
                case 'Location':
                  newState.requestLocation = reference as Reference<Location>
                  break
                default:
                  break
              }
            })

            newState.appointmentFilters = compact([
              newState.requestPractitioner,
              newState.requestPatient,
            ])

            return newState
          }),
        setAvailableAppointmentSlots: (slots) =>
          set({ availableAppointmentSlots: slots }),
        setRequestPractitioner: (practitioner) =>
          set((state) => ({
            requestPractitioner: practitioner,
            appointmentFilters: compact([
              ...state.appointmentFilters.filter(
                (f) => !isOfType(f, 'Practitioner')
              ),
              practitioner,
            ]),
          })),
        setRequestSpecialty: (specialty) =>
          set({ requestSpecialty: specialty }),
        setRequestLocation: (location) =>
          set((state) => ({
            requestLocation: location,
            appointmentFilters: compact(
              state.appointmentFilters.filter((f) => !isOfType(f, 'Location'))
            ),
          })),
        setRequestBasedOn: (basedOn) =>
          set(() => ({
            requestBasedOn: basedOn,
          })),
        setRequestPatient: (patient) =>
          set((state) => ({
            requestPatient: patient,
            appointmentFilters: compact([
              ...state.appointmentFilters.filter(
                (f) => !isOfType(f, 'Patient')
              ),
              patient,
            ]),
          })),
        // Leaves appointmentFilters alone: the request flow never injects a
        // HealthcareService filter, so stripping service filters here would
        // only remove ones the user added through the filter UI.
        setRequestHealthcareService: (healthcareService, durationMinutes) =>
          set((state) => ({
            requestHealthcareService: healthcareService,
            requestLocation: healthcareService && state.requestLocation,
            durationMinutes,
          })),
        setRequestRequiresTransportation: (requiresTransportation) =>
          set({ requestRequiresTransportation: requiresTransportation }),
        setPreviousViewTypeAndHorizon: (viewTypeAndHorizon) =>
          set({ previousViewTypeAndHorizon: viewTypeAndHorizon }),
      }),
      {
        ...storageOptions,
        partialize: (state) => ({
          ...initialState,
          ...state,
          // If there is no persisted appointmentFilters, use the initialState which will be the IDT practitioner
          // We prioritize the persisted appointmentFilters over the initialState for this field
          appointmentFilters: state.appointmentFilters?.length
            ? state.appointmentFilters
            : [...(initialState?.appointmentFilters ?? [])],
          // Transient: derived from an in-flight AI suggestion query, so never
          // serialize it into the URL.
          availableAppointmentSlots: [],
        }),
      }
    )
  )

export const buildCalendarQueryParam =
  createStoreQueryParamBuilder<CalendarState>(storageOptions.name)

export function buildAppointmentRelativeUrl(
  appointment: appointments.ExtendedAppointment
): string {
  return `/calendar?${buildCalendarQueryParam({
    viewType: ViewType.TimeGrid,
    aggregateCalendarFilters: [],
    windowStart: appointment.start,
    windowEnd: appointment.start,
    requestRequiresTransportation: false,
    viewHorizon: ViewHorizon.Day,
    selectedAppointment: appointment.reference,
    showRequestAppointment: false,
    appointmentStatuses: [],
    appointmentFilters: [],
  })}`
}

// Week calendar with participant and practitioner filters — the expand target
// when rescheduling from a facesheet appointment sheet.
export function buildSchedulingCalendarExpandUrl(
  appointment: appointments.ExtendedAppointment
): string {
  const anchor = appointment.start ?? now()
  return `/calendar?${buildCalendarQueryParam({
    viewType: ViewType.TimeGrid,
    viewHorizon: ViewHorizon.Week,
    windowStart: anchor,
    windowEnd: anchor,
    requestRequiresTransportation: false,
    selectedAppointment: appointment.reference,
    showRequestAppointment: false,
    appointmentStatuses: ['booked'],
    appointmentFilters: compact([
      appointment.patients.at(0),
      appointment.practitioners.at(0),
    ]),
    aggregateCalendarFilters: [],
  })}`
}

export const {
  Provider: CalendarProvider,
  useContextStore: useCalendarContext,
} = createZustandContext(createCalendarStore)
